import Color from "color";
import {
  DEFAULT_THEME_TOKENS,
  type AppThemeTokens,
  type ThemeTokenKey,
  cssTokenName,
  isStyleToken,
} from "@/config/themeTokens";

export type ThemeDiagnostic = {
  field: string;
  code: "invalid-value" | "unsupported-field";
  value: unknown;
};
export type ResolvedBrandTheme = {
  tokens: AppThemeTokens;
  diagnostics: ThemeDiagnostic[];
};
const supported = new Set<string>(Object.keys(DEFAULT_THEME_TOKENS));

export const mixThemeColors = (a: string, b: string, weight: number) => {
  const first = Color(a).rgb(),
    second = Color(b).rgb();
  const firstAlpha = first.alpha() * weight;
  const secondAlpha = second.alpha() * (1 - weight);
  const alpha = firstAlpha + secondAlpha;
  const channels = first
    .array()
    .slice(0, 3)
    .map((channel, index) =>
      alpha
        ? (channel * firstAlpha + second.array()[index] * secondAlpha) / alpha
        : 0
    );
  const color = Color.rgb(channels).alpha(alpha);
  return alpha === 1 ? color.hex() : color.rgb().string();
};

/** Resolve a raw API/persisted payload without accessing or mutating browser state. */
export function resolveBrandTheme(payload?: unknown): ResolvedBrandTheme {
  const input =
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : {};
  // Apply only the canonical fields the API sends; every other token uses
  // DEFAULT_THEME_TOKENS. Old field names are not mapped.
  const raw = input;
  const diagnostics: ThemeDiagnostic[] = [];
  for (const [field] of Object.entries(raw))
    if (!supported.has(field))
      diagnostics.push({
        field,
        value: input[field],
        code: "unsupported-field",
      });
  const tokens = {} as AppThemeTokens;
  const resolving = new Set<string>();
  const cssKeys = Object.fromEntries(
    [...supported].map((key) => [cssTokenName(key), key])
  );
  function parse(value: unknown): string {
    if (typeof value !== "string" || !value.trim())
      throw new Error("Empty or non-string color");
    const text = value.trim();
    const variable = text.match(/^var\(\s*(--[\w-]+)\s*(?:,\s*(.+))?\)$/);
    if (variable) {
      const key = cssKeys[variable[1]];
      if (!key) {
        if (variable[2]) return parse(variable[2]);
        throw new Error("Unknown reference");
      }
      return resolve(key as ThemeTokenKey);
    }
    const expression = text.match(
      /^color-mix\(in srgb,\s*(.+?)\s+(\d+(?:\.\d+)?)%,\s*(.+)\)$/
    );
    if (expression) {
      const weight = Number(expression[2]) / 100;
      if (weight < 0 || weight > 1) throw new Error("Invalid weight");
      return mixThemeColors(parse(expression[1]), parse(expression[3]), weight);
    }
    const color = Color(text); // Validate without relying on browser color parsing.
    // MUI calculations require hex/rgb/hsl syntax rather than named colors.
    return /^[a-z]+$/i.test(text)
      ? color.alpha() === 1
        ? color.hex()
        : color.rgb().string()
      : text;
  }
  function fallback(key: ThemeTokenKey): string {
    if (key === "color_menu_text") {
      const light = resolve("neutral_white");
      const surface = Color(resolve("surface_dark"));
      const dark = Color(resolve("neutral_700"));
      const lightColor = Color(light);
      // Default sidebar text is White (designer text/on-dark, user-approved
      // November 2026). Mid-tone/translucent surfaces keep that fallback.
      if (
        surface.alpha() !== 1 ||
        dark.alpha() !== 1 ||
        lightColor.alpha() !== 1
      )
        return light;
      const darkContrast = dark.contrast(surface);
      return darkContrast >= 4.5 && darkContrast > lightColor.contrast(surface)
        ? resolve("neutral_700")
        : light;
    }
    return DEFAULT_THEME_TOKENS[key as keyof typeof DEFAULT_THEME_TOKENS];
  }
  function resolve(key: ThemeTokenKey): string {
    if (tokens[key] != null) return tokens[key];
    if (resolving.has(key)) throw new Error("Cyclic color reference");
    resolving.add(key);
    try {
      if (raw[key] != null) {
        try {
          if (isStyleToken(key)) {
            if (
              typeof raw[key] !== "string" ||
              !(raw[key] as string).trim() ||
              /[;{}]/.test(raw[key] as string)
            )
              throw new Error("Invalid typography");
            if (
              key.startsWith("radius_") &&
              [...(raw[key] as string).matchAll(/var\(\s*(--[\w-]+)/g)].some(
                ([, name]) =>
                  !/^--radius-(control|container|round|focus-ring)$/.test(name)
              )
            )
              throw new Error("Unsupported radius reference");
            tokens[key] = (raw[key] as string).trim();
          } else tokens[key] = parse(raw[key]);
          return tokens[key];
        } catch {
          diagnostics.push({
            field: key,
            value: raw[key],
            code: "invalid-value",
          });
        }
      }
      tokens[key] = fallback(key);
      return tokens[key];
    } finally {
      resolving.delete(key);
    }
  }
  for (const key of supported) resolve(key as ThemeTokenKey);
  return { tokens, diagnostics };
}
